import {
  createContext,
  MutableRefObject,
  useEffect,
  useRef,
  useState,
} from 'react';
import {
  NavigateFunction,
  NavigateOptions,
  resolvePath,
  To,
  useLocation,
  useNavigate,
} from 'react-router-dom';
import {
  movePointerBack,
  findStepIndex,
  ParcelCreationNavigation,
} from '../models/parcelCreationFlowNavigation';
import type { BlockedHistoryNavigation } from './use-navigation-blocker.hook';
import { useModal } from '../common/modal';
import { Modals } from '../constants/modals';

interface ParcelNavigationHandlers {
  back: (navigate: NavigateFunction) => void;
  next: (navigate: NavigateFunction) => Promise<void>;
  backDisabled: boolean;
  nextDisabled: boolean;
}

export const ParcelHistoryNavigationContext =
  createContext<MutableRefObject<ParcelNavigationHandlers | null> | null>(null);

interface HistoryRedirect {
  locationKey: string;
  originKey: string;
  to: To;
  options?: NavigateOptions;
}

const waitForHistoryRestoration = (
  blockedKey: string,
  currentKey: string,
  signal: AbortSignal
) => {
  // This router version schedules the undo of a blocked POP asynchronously.
  // Replaying it before that native event can navigate to the wrong entry.
  if (window.history.state?.key !== blockedKey) return Promise.resolve(true);
  return new Promise<boolean>((resolve) => {
    const finish = (restored: boolean) => {
      window.removeEventListener('popstate', onPopState);
      signal.removeEventListener('abort', onAbort);
      resolve(restored);
    };
    function onPopState(event: PopStateEvent) {
      if ((event.state?.key ?? 'default') !== currentKey) return;
      finish(true);
    }
    function onAbort() {
      finish(false);
    }
    window.addEventListener('popstate', onPopState);
    signal.addEventListener('abort', onAbort, { once: true });
  });
};

export const useParcelHistoryNavigation = (
  navigation: ParcelCreationNavigation,
  canSkipVerifyPhone: boolean
) => {
  const handlers = useRef<ParcelNavigationHandlers | null>(null);
  const handling = useRef(false);
  const historyRequest = useRef<AbortController | null>(null);
  const [redirect, setRedirect] = useState<HistoryRedirect | null>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { closeModal, isModalVisible } = useModal();

  useEffect(() => () => historyRequest.current?.abort(), []);

  useEffect(() => {
    if (!redirect) return;
    if (location.key === redirect.originKey) return;
    const expectedPath = resolvePath(redirect.to, location.pathname).pathname;
    if (location.pathname === expectedPath) {
      setRedirect(null);
    } else if (location.key === redirect.locationKey) {
      // A skipped or changed step can differ from the historical URL.
      // Correct that history entry before mounting its form.
      navigate(redirect.to, { ...redirect.options, replace: true });
    }
  }, [redirect, location, navigate]);

  const onHistoryNavigation = async (blocker: BlockedHistoryNavigation) => {
    if (handling.current) return;
    const controls = handlers.current;
    const targetName = blocker.location.pathname
      .split('/')
      .filter(Boolean)
      .at(-1);
    const currentIndex = navigation.map.findIndex(
      (step) => step.name === navigation.current?.name
    );
    const targetIndex = navigation.map.findIndex(
      (step) => step.name === targetName
    );
    const isBack = targetIndex < currentIndex;

    if (
      !controls ||
      currentIndex < 0 ||
      targetIndex < 0 ||
      targetIndex === currentIndex ||
      (isBack
        ? controls.backDisabled
        : controls.nextDisabled || !navigation.next)
    ) {
      blocker.reset();
      return;
    }

    handling.current = true;
    const request = new AbortController();
    historyRequest.current = request;
    let destination: { to: To; options?: NavigateOptions } | undefined;
    const captureNavigation: NavigateFunction = (
      to: To | number,
      options?: NavigateOptions
    ) => {
      if (typeof to !== 'number') destination = { to, options };
    };

    try {
      const restored = await waitForHistoryRestoration(
        blocker.location.key,
        location.key,
        request.signal
      );
      if (!restored || request.signal.aborted) return;
      if (isBack) {
        if (isModalVisible(Modals.SIGNATURE)) closeModal(Modals.SIGNATURE);
        let previousNavigation = navigation;
        let previousIndex = currentIndex;
        while (previousIndex > targetIndex) {
          const nextNavigation = movePointerBack(
            previousNavigation,
            canSkipVerifyPhone ? 'verify-phone' : undefined
          );
          if (nextNavigation.current?.name === previousNavigation.current?.name)
            break;
          controls.back(captureNavigation);
          previousNavigation = nextNavigation;
          previousIndex = nextNavigation.current
            ? findStepIndex(nextNavigation, nextNavigation.current.name)
            : -1;
        }
      } else {
        // This is the same handler as UI Next, including validation and API saves.
        await controls.next(captureNavigation);
      }

      if (request.signal.aborted) return;

      if (destination) {
        const expectedPath = resolvePath(
          destination.to,
          location.pathname
        ).pathname;
        if (expectedPath !== blocker.location.pathname) {
          setRedirect({
            locationKey: blocker.location.key,
            originKey: location.key,
            ...destination,
          });
        }
        blocker.proceed();
      } else {
        // Validation, a failed save, or a modal kept us on the current step.
        blocker.reset();
      }
    } catch (error) {
      if (!request.signal.aborted) blocker.reset();
      console.error(error);
    } finally {
      handling.current = false;
    }
  };

  return { handlers, onHistoryNavigation, isHistoryRedirecting: !!redirect };
};
